import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from "recharts";
import { dashboardApi, type RationalHaccpEntry, type RationalDeviceSnapshot, type KitchenAnalyticsResponse } from "../../api/dashboardApi";
import { useAuth } from "../../auth/AuthContext";
import "../../styles/rational.css";

type WidgetData = {
  name: string;
  type: string;
  fromDate?: string;
  toDate?: string;
  groupName?: string;
  timePeriod?: string;
  traits?: string[];
  data?: Array<{ name: string; value: number }>;
  value?: number | string;
  [k: string]: unknown;
};

/** Colori grafico a torta per stato errore (funzionamento regolare / allarmi) e stato online (online / offline) */
const PIE_COLORS: Record<string, string> = {
  healthyDevices: "#28a745",
  unhealthyDevices: "#dc3545",
  "Funzionamento regolare": "#28a745",
  Allarmi: "#dc3545",
  onlineDevices: "#28a745",
  offlineDevices: "#6c757d",
  "Dispositivo online": "#28a745",
  "Dispositivo offline": "#6c757d",
  loadingTimeWithin: "#6366f1",
  loadingTimeOutside: "#94a3b8",
  idleTimeWithin: "#0ea5e9",
  idleTimeOutside: "#cbd5e1",
};

const TRAIT_LABELS: Record<string, string> = {
  healthyDevices: "Funzionamento regolare",
  unhealthyDevices: "Allarmi",
  onlineDevices: "Dispositivo online",
  offlineDevices: "Dispositivo offline",
  loadingTimeWithin: "Tempo entro soglia",
  loadingTimeOutside: "Tempo fuori soglia",
  idleTimeWithin: "Inattività entro",
  idleTimeOutside: "Inattività fuori",
  "Healthy Devices": "Funzionamento regolare",
  "Unhealthy Devices": "Allarmi",
  "Online Devices": "Dispositivo online",
  "Offline Devices": "Dispositivo offline",
};

function formatPeriod(from?: string, to?: string, timePeriod?: string): string {
  if (!from && !to) return timePeriod === "currentMonth" ? "Mese corrente" : "Oggi";
  try {
    const fromD = from ? new Date(from) : null;
    const toD = to ? new Date(to) : null;
    if (fromD && toD) {
      return `${fromD.toLocaleDateString("it-IT")} ${fromD.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} – ${toD.toLocaleDateString("it-IT")} ${toD.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
    }
    if (fromD) return fromD.toLocaleString("it-IT");
  } catch {
    // ignore
  }
  return from ?? to ?? "";
}

function toDateInputValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDuration(seconds?: number) {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

function parseEnergyKwh(v?: string): number {
  if (!v) return 0;
  const num = Number(String(v).replace(",", ".").replace(/[^\d.]+/g, ""));
  return Number.isFinite(num) ? num : 0;
}

function groupSum<T>(
  items: T[],
  keyFn: (item: T) => string,
  valueFn: (item: T) => number
): Array<{ name: string; value: number }> {
  const map = new Map<string, number>();
  items.forEach((item) => {
    const key = keyFn(item) || "—";
    map.set(key, (map.get(key) ?? 0) + valueFn(item));
  });
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
}

export default function RationalAnalyticsPage() {
  const { token } = useAuth() as { token: string | null };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [widgets, setWidgets] = useState<WidgetData[]>([]);
  const [groupName, setGroupName] = useState<string>("");
  const [fromDate, setFromDate] = useState(() => toDateInputValue(new Date(Date.now() - 7 * 24 * 3600 * 1000)));
  const [toDate, setToDate] = useState(() => toDateInputValue(new Date()));
  const [haccpData, setHaccpData] = useState<{ groupName?: string; items: RationalHaccpEntry[] } | null>(null);
  const [selectedHaccpId, setSelectedHaccpId] = useState<number | null>(null);
  const [haccpDetail, setHaccpDetail] = useState<RationalHaccpEntry | null>(null);
  const [haccpDetailLoading, setHaccpDetailLoading] = useState(false);
  const [devices, setDevices] = useState<RationalDeviceSnapshot[]>([]);
  const [kitchenAnalytics, setKitchenAnalytics] = useState<KitchenAnalyticsResponse | null>(null);
  const [haccpFilter, setHaccpFilter] = useState<string>(""); // "" = tutti

  const normalizedFromDate = useMemo(() => fromDate, [fromDate]);
  const normalizedToDate = useMemo(() => toDate, [toDate]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.allSettled([
      dashboardApi.getRationalDashboard(token),
      dashboardApi.getRationalHaccp(token, {
        from: normalizedFromDate,
        to: normalizedToDate,
        size: 20,
        filter: haccpFilter || undefined,
      }),
      dashboardApi.getRationalDevices(token),
      dashboardApi.getKitchenAnalytics(token, { from: normalizedFromDate, to: normalizedToDate }),
    ])
      .then(([dashboardRes, haccpRes, devicesRes, kitchenRes]) => {
        if (dashboardRes.status === "fulfilled") {
          const res: any = dashboardRes.value;
          const payload = res?.data ?? res;
          const dashboards = payload?.dashboards ?? [];
          const dash = Array.isArray(dashboards) ? dashboards[0] : dashboards;
          const list = dash?.widgets ?? [];
          setWidgets(Array.isArray(list) ? list : []);
          setGroupName(dash?.groupName ?? (dash?.widgets?.[0] as any)?.groupName ?? "Il mio ristorante");
        } else {
          const e: any = dashboardRes.reason;
          setError(e?.message ?? "Errore caricamento analytics Rational");
          setWidgets([]);
        }

        if (haccpRes.status === "fulfilled") {
          setHaccpData(haccpRes.value as any);
        } else {
          setHaccpData({ items: [] });
        }

        if (devicesRes?.status === "fulfilled") {
          const devPayload = (devicesRes.value as any)?.data ?? devicesRes.value;
          const items = Array.isArray(devPayload?.items) ? devPayload.items : [];
          setDevices(items);
        } else {
          setDevices([]);
        }

        if (kitchenRes?.status === "fulfilled") {
          const payload = (kitchenRes.value as any)?.data ?? kitchenRes.value;
          setKitchenAnalytics(payload ?? null);
        } else {
          setKitchenAnalytics(null);
        }
      })
      .finally(() => setLoading(false));
  }, [token, normalizedFromDate, normalizedToDate, haccpFilter]);

  /** Aggiornamento dispositivi e notifiche ogni 5 minuti (consumi e allarmi in tempo reale) */
  useEffect(() => {
    if (!token) return;
    const refreshRealtime = () => {
      Promise.allSettled([
        dashboardApi.getRationalDevices(token),
        dashboardApi.getKitchenAnalytics(token, { from: normalizedFromDate, to: normalizedToDate }),
      ]).then(([devicesRes, kitchenRes]) => {
        if (devicesRes?.status === "fulfilled") {
          const devPayload = (devicesRes.value as any)?.data ?? devicesRes.value;
          const items = Array.isArray(devPayload?.items) ? devPayload.items : [];
          setDevices(items);
        }
        if (kitchenRes?.status === "fulfilled") {
          const payload = (kitchenRes.value as any)?.data ?? kitchenRes.value;
          setKitchenAnalytics(payload ?? null);
        }
      });
    };
    const interval = setInterval(refreshRealtime, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token, normalizedFromDate, normalizedToDate]);

  useEffect(() => {
    const items = haccpData?.items ?? [];
    if (items.length === 0) {
      setSelectedHaccpId(null);
      setHaccpDetail(null);
      return;
    }
    if (!items.some((x) => x.id === selectedHaccpId)) {
      setSelectedHaccpId(items[0].id);
    }
  }, [haccpData, selectedHaccpId]);

  useEffect(() => {
    if (!selectedHaccpId) {
      setHaccpDetail(null);
      return;
    }
    setHaccpDetailLoading(true);
    dashboardApi
      .getRationalHaccpDetail(token, selectedHaccpId, { incomplete: false })
      .then((res: any) => {
        const payload = res?.data ?? res;
        setHaccpDetail(payload ?? null);
      })
      .catch(() => setHaccpDetail(null))
      .finally(() => setHaccpDetailLoading(false));
  }, [token, selectedHaccpId]);

  const getPieData = (w: WidgetData): Array<{ name: string; value: number; color: string }> => {
    const data = Array.isArray(w.data) ? w.data : [];
    const defaultColors = ["#28a745", "#dc3545", "#6c757d", "#6366f1", "#0ea5e9", "#94a3b8"];
    return data
      .filter((d) => (d.value ?? 0) > 0 || data.every((x) => (x.value ?? 0) === 0))
      .map((d, i) => {
        const rawName = String(d.name ?? "").trim();
        const mapped = TRAIT_LABELS[rawName];
        const label = mapped != null ? mapped : (rawName || "—");
        const color =
          PIE_COLORS[rawName] ??
          PIE_COLORS[label] ??
          defaultColors[i % defaultColors.length];
        return { name: label, value: Number(d.value ?? 0), color };
      })
      .filter((d) => d.value >= 0);
  };

  const haccpItems = useMemo(() => haccpData?.items ?? [], [haccpData]);
  const haccpByDevice = useMemo(
    () => groupSum(haccpItems, (x) => x.deviceName ?? "—", () => 1),
    [haccpItems]
  );
  const haccpByProgram = useMemo(
    () => groupSum(haccpItems, (x) => x.name ?? "—", () => 1),
    [haccpItems]
  );
  const haccpEnergyByDevice = useMemo(
    () => groupSum(haccpItems, (x) => x.deviceName ?? "—", (x) => parseEnergyKwh(x.energyConsumption)),
    [haccpItems]
  );
  const energyByDeviceName = useMemo(
    () => new Map(haccpEnergyByDevice.map((d) => [d.name, d.value])),
    [haccpEnergyByDevice]
  );
  const haccpDoorsByDevice = useMemo(
    () => groupSum(haccpItems, (x) => x.deviceName ?? "—", (x) => Number(x.openDoorCount ?? 0)),
    [haccpItems]
  );

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex align-items-center gap-2 text-muted">
          <div className="spinner-border spinner-border-sm" />
          Caricamento analytics Rational…
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 rational-analytics-page">
      <div className="rational-page-header mb-4">
        <div className="rational-page-header-content">
          <div className="rational-page-badge">Connected Cooking</div>
          <h1 className="rational-page-title">
            <i className="bi bi-graph-up-arrow me-2" />
            Rational Analytics
          </h1>
          <p className="rational-page-sub">
            Gruppo: <strong>{groupName}</strong> · Dati Connected Cooking
          </p>
        </div>
        <Link to="/machines/RATIONAL" className="btn rational-btn-back">
          <i className="bi bi-arrow-left me-1" />
          Torna a Rational
        </Link>
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-end mb-4 rational-filters">
        <div>
          <label className="form-label mb-1 small text-muted">Da</label>
          <input
            className="form-control form-control-sm"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div>
          <label className="form-label mb-1 small text-muted">A</label>
          <input
            className="form-control form-control-sm"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-warning mb-4">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      <div className="row g-4">
        {widgets.map((w, idx) => {
          const periodLabel = formatPeriod(w.fromDate, w.toDate, w.timePeriod);
          const pieData = w.type === "pieChart" ? getPieData(w) : [];

          return (
            <div
              key={`${w.name}-${idx}`}
              className={`${w.type === "pieChart" || w.type === "topEntries" ? "col-12 col-md-6 col-lg-4" : "col-12 col-md-6 col-lg-3"}`}
            >
              <div className="rational-widget h-100">
                <div className="rational-widget-title">{w.name}</div>
                <div className="small text-muted mb-2">
                  {periodLabel}
                  {w.groupName && (
                    <>
                      {" · "}
                      {w.groupName}
                    </>
                  )}
                </div>

                {w.type === "number" && (
                  <div className="rational-widget-value">
                    {w.value != null && w.value !== "" ? w.value : "—"}
                  </div>
                )}

                {w.type === "pieChart" && (
                  <div style={{ height: 220, minHeight: 200 }}>
                    {pieData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220} minHeight={200}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {pieData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v: number) => [v, ""]} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100 text-muted small">
                        Dati non disponibili
                      </div>
                    )}
                  </div>
                )}

                {w.type === "topEntries" && (
                  <div>
                    {Array.isArray(w.data) && w.data.length > 0 ? (
                      <ol className="rational-widget-list" style={{ listStyle: "decimal", paddingLeft: "1.25rem" }}>
                        {w.data.slice(0, 8).map((d: any, i: number) => (
                          <li key={i}>
                            {d.name ?? d.label}: <strong>{d.value ?? d.count ?? 0}</strong>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <div className="text-muted small">Dati non disponibili</div>
                    )}
                  </div>
                )}

                {!["number", "pieChart", "topEntries"].includes(w.type) && (
                  <div className="small text-muted">
                    Tipo: {w.type}
                    {w.data != null && (
                      <>
                        {" — "}
                        {JSON.stringify(w.data)}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {widgets.length === 0 && !error && (
        <div className="alert alert-info">
          Nessun widget disponibile. Verifica che l&apos;account Rational abbia i permessi per le statistiche (Connected Cooking).
        </div>
      )}

      {/* Dispositivi Rational (da chambers.view) */}
      {(devices.length > 0 || kitchenAnalytics?.notifications) && (
        <div className="card shadow-sm mt-4">
          <div className="card-header bg-light d-flex align-items-center justify-content-between">
            <span>
              <i className="bi bi-hdd-network me-2" />
              Dispositivi Rational
            </span>
            {devices.length > 0 && (
              <span className="badge bg-secondary">{devices.length} dispositivi</span>
            )}
          </div>
          <div className="card-body">
            {/* Notifiche */}
            {kitchenAnalytics?.notifications && (
              <div className="row mb-4">
                <div className="col-12 col-lg-6 col-xl-4">
                  <div className="rational-card">
                    <div className="rational-card-header">
                      <i className="bi bi-bell" />
                      Notifiche
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <strong className="text-warning">
                          <i className="bi bi-wifi-off me-1" />
                          Forni offline
                        </strong>
                        {kitchenAnalytics.notifications.ovenOffline.length === 0 ? (
                          <p className="text-success small mb-0">Nessuno</p>
                        ) : (
                          <ul className="list-unstyled mb-0 small">
                            {kitchenAnalytics.notifications.ovenOffline.map((name, i) => (
                              <li key={i} className="text-danger">{name}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="mb-3">
                        <strong className="text-danger">
                          <i className="bi bi-exclamation-triangle me-1" />
                          Errori attivi
                        </strong>
                        {kitchenAnalytics.notifications.errorActive.length === 0 ? (
                          <p className="text-success small mb-0">Nessuno</p>
                        ) : (
                          <ul className="list-unstyled mb-0 small">
                            {kitchenAnalytics.notifications.errorActive.map((name, i) => (
                              <li key={i} className="text-danger">
                                {name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <strong className="text-info">
                          <i className="bi bi-check-circle me-1" />
                          Programmi finiti (ultima ora)
                        </strong>
                        {kitchenAnalytics.notifications.programFinished.length === 0 ? (
                          <p className="text-muted small mb-0">Nessuno</p>
                        ) : (
                          <ul className="list-unstyled mb-0 small">
                            {kitchenAnalytics.notifications.programFinished.map((p, i) => (
                              <li key={i}>
                                {p.machineName}: {p.programName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="row g-3">
              {devices.map((d) => {
                const isOnline = String(d.deviceStatus ?? "").toLowerCase().includes("online");
                const activeErrors = Array.isArray(d.currentErrors)
                  ? d.currentErrors.filter((e: any) => e?.active !== false)
                  : [];
                const energyKwh = energyByDeviceName.get(d.name ?? "") ?? energyByDeviceName.get(d.serialNumber ?? "");
                return (
                  <div key={d.id} className="col-12 col-md-6 col-xl-4">
                    <Link
                      to="/machines/RATIONAL"
                      className="text-decoration-none text-dark"
                    >
                      <div className="rational-widget h-100 rational-device-card">
                        <div className="d-flex align-items-start gap-3">
                          {(d.chamberDefaultThumbnailUrl || d.chamberDefaultImageUrl) ? (
                            <img
                              src={d.chamberDefaultThumbnailUrl ?? d.chamberDefaultImageUrl ?? ""}
                              alt={d.name ?? ""}
                              className="rounded"
                              style={{ width: 64, height: 64, objectFit: "contain" }}
                            />
                          ) : (
                            <div
                              className="bg-light rounded d-flex align-items-center justify-content-center"
                              style={{ width: 64, height: 64 }}
                            >
                              <i className="bi bi-hdd-network-fill text-muted fs-4" />
                            </div>
                          )}
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <h6 className="mb-1 text-truncate">{d.name ?? "—"}</h6>
                              <span
                                className={`badge flex-shrink-0 ${
                                  isOnline ? "bg-success" : "bg-secondary"
                                }`}
                              >
                                {d.deviceStatus ?? "—"}
                              </span>
                            </div>
                            <div className="small text-muted">{d.serialNumber ?? d.deviceModel ?? "—"}</div>
                            <div className="mt-2 small">
                              <div className="d-flex align-items-center gap-1">
                                <i className="bi bi-thermometer-half text-muted" style={{ width: 16 }} />
                                <span><strong>Camera:</strong> {d.chamberTemperature ?? "—"}</span>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <i className="bi bi-thermometer-half text-muted" style={{ width: 16 }} />
                                <span><strong>Sonda:</strong> {d.foodTemperature ?? "—"}</span>
                              </div>
                              {d.humidity != null && String(d.humidity) !== "" && (
                                <div className="d-flex align-items-center gap-1">
                                  <i className="bi bi-droplet-half text-muted" style={{ width: 16 }} />
                                  <span><strong>Umidità:</strong> {d.humidity}</span>
                                </div>
                              )}
                              <div className="d-flex align-items-center gap-1">
                                <i className="bi bi-gear text-muted" style={{ width: 16 }} />
                                <span><strong>Programma:</strong> {d.cookingProgram ?? "—"} {d.cookingProgramStep ? `· ${d.cookingProgramStep}` : ""}</span>
                              </div>
                              <div className="d-flex align-items-center flex-wrap gap-1">
                                <span className={`badge ${String(d.doorStatus ?? "").toLowerCase().includes("aperto") ? "bg-warning text-dark" : "bg-light text-dark"}`}>
                                  <i className="bi bi-door-open me-1" />
                                  {d.doorStatus ?? "—"}
                                </span>
                                <span className="text-muted small">
                                  <i className="bi bi-droplet me-1" /> {d.cleaningPercentage ?? "—"}%
                                  <i className="bi bi-wrench me-1 ms-2" /> {d.maintenancePercentage ?? "—"}%
                                </span>
                              </div>
                              {energyKwh != null && energyKwh > 0 && (
                                <div className="d-flex align-items-center gap-1 mt-1">
                                  <i className="bi bi-lightning-charge text-muted" style={{ width: 16 }} />
                                  <span><strong>Consumo:</strong> {energyKwh.toFixed(1)} kWh</span>
                                </div>
                              )}
                              {activeErrors.length > 0 && (
                                <div className="mt-2">
                                  <strong className="text-danger small">
                                    <i className="bi bi-exclamation-triangle me-1" />
                                    Allarmi attivi
                                  </strong>
                                  <ul className="list-unstyled mb-0 small mt-1">
                                    {activeErrors.map((err: any, i: number) => (
                                      <li key={i} className="text-danger">
                                        {err?.errorCodeTranslation ?? err?.errorCode ?? "Errore"}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {d.userActionRequired && <span className="badge bg-warning text-dark mt-1">Azione richiesta</span>}
                              {d.userActionRequired && <span className="badge bg-warning text-dark ms-1 mt-1">Azione richiesta</span>}
                            </div>
                          </div>
                          <i className="bi bi-chevron-right text-muted" />
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="card shadow-sm mt-4">
        <div className="card-header bg-light d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>
            <i className="bi bi-journal-check me-2" />
            HACCP Connected Cooking
          </span>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="small text-muted me-2">Tipo protocollo:</span>
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${haccpFilter === "" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setHaccpFilter("")}
              >
                <i className="bi bi-grid-3x3-gap me-1" />
                Tutti
              </button>
              <button
                type="button"
                className={`btn ${haccpFilter === "cooking" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setHaccpFilter("cooking")}
              >
                <i className="bi bi-fire me-1" />
                Cottura
              </button>
              <button
                type="button"
                className={`btn ${haccpFilter === "care" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setHaccpFilter("care")}
              >
                <i className="bi bi-droplet me-1" />
                Pulizia
              </button>
            </div>
          </div>
          <span className="small text-muted">
            {(haccpData?.groupName ?? groupName) || "Il mio ristorante"} · {normalizedFromDate} → {normalizedToDate}
          </span>
        </div>
        <div className="card-body">
          <div className="small text-muted mb-3">
            Protocolli HACCP del gruppo Rational nel periodo selezionato.
          </div>

          <div className="row g-3 mb-4">
            <div className="col-12 col-lg-6">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">Batch per apparecchio</div>
                <div style={{ height: 260, minHeight: 200 }}>
                  {haccpByDevice.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260} minHeight={200}>
                      <BarChart data={haccpByDevice}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#0d6efd" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Dati non disponibili</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">Energia per apparecchio</div>
                <div style={{ height: 260, minHeight: 200 }}>
                  {haccpEnergyByDevice.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260} minHeight={200}>
                      <BarChart data={haccpEnergyByDevice}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip formatter={(v: number) => [`${v.toFixed(2)} kWh`, "Energia"]} />
                        <Bar dataKey="value" fill="#198754" radius={[4, 4, 0, 0]}>
                          <LabelList dataKey="value" position="top" formatter={(v: number) => `${v.toFixed(1)} kWh`} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Dati non disponibili</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">Aperture porta per apparecchio</div>
                <div style={{ height: 260 }}>
                  {haccpDoorsByDevice.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={haccpDoorsByDevice}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#fd7e14" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Dati non disponibili</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">Batch per programma</div>
                <div style={{ height: 260, minHeight: 200 }}>
                  {haccpByProgram.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260} minHeight={200}>
                      <BarChart data={haccpByProgram}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#6f42c1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Dati non disponibili</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive" style={{ maxHeight: 420, overflowY: "auto" }}>
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ width: 52 }}></th>
                  <th>Lotto</th>
                  <th>Programma cottura</th>
                  <th>Data</th>
                  <th>Durata</th>
                  <th>Aperture porta</th>
                  <th>Energia</th>
                  <th>Apparecchio</th>
                  <th>Numero di serie</th>
                  <th>Gruppo</th>
                </tr>
              </thead>
              <tbody>
                {(haccpData?.items ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-3">
                      Nessun protocollo HACCP disponibile nel periodo selezionato.
                    </td>
                  </tr>
                ) : (
                  (haccpData?.items ?? []).map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedHaccpId(row.id)}
                      style={{ cursor: "pointer" }}
                      className={selectedHaccpId === row.id ? "table-primary" : ""}
                    >
                      <td className="text-center">
                        {row.categoryImage ? (
                          <img
                            src={row.categoryImage}
                            alt=""
                            className="rounded"
                            style={{ width: 36, height: 36, objectFit: "contain" }}
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-muted">
                            <i className={`bi ${row.careRecipe ? "bi-droplet" : "bi-fire"} fs-5`} />
                          </span>
                        )}
                      </td>
                      <td className="fw-semibold">{row.batchId ?? "—"}</td>
                      <td>
                        <div>{row.name ?? "—"}</div>
                        <div className="small text-muted">
                          {row.careRecipe ? "Protocollo pulizia" : "Protocollo cottura"}
                        </div>
                      </td>
                      <td className="text-nowrap">
                        <i className="bi bi-calendar3 me-1 text-muted" />
                        {row.startDate ? new Date(row.startDate).toLocaleString("it-IT") : "—"}
                      </td>
                      <td>
                        <i className="bi bi-clock me-1 text-muted" />
                        {formatDuration(row.duration)}
                      </td>
                      <td>
                        <i className="bi bi-door-open me-1 text-muted" />
                        {row.openDoorCount ?? "—"}
                      </td>
                      <td>
                        <i className="bi bi-lightning-charge me-1 text-muted" />
                        {row.energyConsumption ?? "—"}
                      </td>
                      <td>
                        <i className="bi bi-hdd-network me-1 text-muted" />
                        {row.deviceName ?? "—"}
                      </td>
                      <td className="text-nowrap">{row.deviceSerialNumber ?? "—"}</td>
                      <td>{row.groupName ?? row.customerName ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="fw-semibold">Dettaglio protocollo selezionato</div>
              {selectedHaccpId && (
                <span className="badge text-bg-secondary">ID {selectedHaccpId}</span>
              )}
            </div>

            {haccpDetailLoading ? (
              <div className="text-muted small">Caricamento dettaglio HACCP…</div>
            ) : !haccpDetail ? (
              <div className="text-muted small">Seleziona un protocollo HACCP per vedere il dettaglio.</div>
            ) : (
              <div className="border rounded p-3">
                <div className="d-flex align-items-start gap-3 mb-3">
                  {haccpDetail.categoryImage && (
                    <img
                      src={haccpDetail.categoryImage}
                      alt=""
                      className="rounded"
                      style={{ width: 56, height: 56, objectFit: "contain" }}
                    />
                  )}
                  <div className="flex-grow-1">
                    <div className="row g-3 mb-0">
                      <div className="col-6 col-md-3">
                        <div className="text-muted small">Programma</div>
                        <div className="fw-semibold">{haccpDetail.name ?? "—"}</div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="text-muted small">Apparecchio</div>
                        <div className="fw-semibold">{haccpDetail.deviceName ?? "—"}</div>
                      </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Seriale</div>
                    <div className="fw-semibold">{haccpDetail.deviceSerialNumber ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Data avvio</div>
                    <div className="fw-semibold">
                      {haccpDetail.startDate ? new Date(haccpDetail.startDate).toLocaleString("it-IT") : "—"}
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Energia</div>
                    <div className="fw-semibold">{haccpDetail.energyConsumption ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Durata</div>
                    <div className="fw-semibold">{formatDuration(haccpDetail.duration)}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Aperture porta</div>
                    <div className="fw-semibold">{haccpDetail.openDoorCount ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">App version</div>
                    <div className="fw-semibold">{haccpDetail.appVersion ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Recipe ID</div>
                    <div className="fw-semibold">{haccpDetail.recipeId ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Chamber ID</div>
                    <div className="fw-semibold">{haccpDetail.chamberId ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Temperatura unità</div>
                    <div className="fw-semibold">{haccpDetail.tempUnit ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Tipo protocollo</div>
                    <div className="fw-semibold">{haccpDetail.careRecipe ? "Pulizia" : "Cottura"}</div>
                  </div>
                    </div>
                  </div>
                </div>

                {Array.isArray(haccpDetail.features) && haccpDetail.features.length > 0 && (
                  <div className="mb-3">
                    <div className="text-muted small mb-2">Features</div>
                    <div className="d-flex flex-wrap gap-2">
                      {haccpDetail.features.map((feature) => (
                        <span key={feature} className="badge text-bg-light border">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <details>
                  <summary className="small text-primary" style={{ cursor: "pointer" }}>
                    Payload completo HACCP
                  </summary>
                  <pre className="small bg-light border rounded p-3 mt-2 mb-0" style={{ maxHeight: 320, overflow: "auto" }}>
                    {JSON.stringify(haccpDetail.raw ?? haccpDetail, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
